import { createIcons, icons } from 'lucide';

function initLucideIcons() {
  createIcons({ icons });
}

// Scoped refresh: only replaces [data-lucide] elements within `root` instead of
// scanning the whole document. Used by high-frequency callers (e.g. AG Grid
// batch loading) to keep the cost O(new icons) rather than O(total icons).
function refreshLucideIconsScoped(root) {
  if (!root || typeof root.querySelectorAll !== 'function') {
    return;
  }

  const targets = root.querySelectorAll('[data-lucide]:not(svg)');
  if (targets.length === 0) {
    return;
  }

  const scopedAttr = 'data-lucide-pending';
  targets.forEach((element) => {
    const iconName = element.getAttribute('data-lucide');
    if (!iconName) {
      return;
    }
    element.setAttribute(scopedAttr, iconName);
    element.removeAttribute('data-lucide');
  });

  // createIcons still scans the document, but only our marked elements match
  // because data-lucide-pending is unique to this scoped pass.
  createIcons({ icons, nameAttr: scopedAttr });

  // Strip both attributes from the resulting SVGs so future refreshes (scoped
  // or global) skip them and the per-batch cost stays bounded.
  root.querySelectorAll(`svg[${scopedAttr}]`).forEach((svg) => {
    svg.removeAttribute(scopedAttr);
    svg.removeAttribute('data-lucide');
  });
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', initLucideIcons);

// Re-initialize on Turbo navigation
document.addEventListener('turbo:load', initLucideIcons);
document.addEventListener('turbo:render', initLucideIcons);

// Re-initialize when Turbo frames load content
document.addEventListener('turbo:frame-load', initLucideIcons);
document.addEventListener('turbo:frame-load', initLucideIcons, true);

// Re-initialize after a Turbo Stream renders (e.g. tray refresh after clear/kill)
document.addEventListener('turbo:before-stream-render', (event) => {
  const fallback = event.detail?.render;
  if (typeof fallback !== 'function') return;
  event.detail.render = async (streamElement) => {
    await fallback(streamElement);
    initLucideIcons();
  };
});

// Re-initialise after infinite-scroll appends paginated rows (e.g. training
// matrix grouped-by-jobs header pickaxe/users icons and duplicate-job pills)
document.addEventListener('infinite-scroll:rows-loaded', initLucideIcons);

// Re-initialize when Bootstrap dropdowns are shown (for dynamically loaded content)
document.addEventListener('shown.bs.dropdown', initLucideIcons);

// Bootstrap modals inject their body markup after page load, so any icons in
// remotely rendered modal content need the same treatment as dropdowns.
document.addEventListener('shown.bs.modal', initLucideIcons);

// jQuery fallback for Bootstrap dropdowns and modals
if (typeof $ !== 'undefined') {
  $(document).on('shown.bs.dropdown', initLucideIcons);
  $(document).on('shown.bs.modal', initLucideIcons);
}

// Module scripts can load after DOMContentLoaded/turbo:load have already fired.
if (document.readyState !== 'loading') {
  initLucideIcons();
}

export { initLucideIcons, refreshLucideIconsScoped };
